import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import produce, { Draft } from 'immer';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '@moderepo/mode-apis';
import { capitalizeAll, LoginForm, EMAIL_VALIDATION_REGEX, getErrorMessage, setLocalStorageItem, Verify2FAForm, ExternalLoginProviderInfo }
    from '@moderepo/mode-app-components';
import { DEFAULT_ORGANIZATION_ID, RitzAPI } from '@moderepo/mode-internal-apis';
import { makeStyles } from '@material-ui/core';
import { ritzDataContext, selectAgentLoginInfo, setLoginInfo } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showErrorNotification } from '@moderepo/mode-ui-state';
import * as Constants from '../../utils/constants';
import { useRitzTranslation } from '../../utils/customHooks';
import { NotAuthorized, RouteHelper } from '..';
import { LanguageSelectorContainer } from '../language-selector/LanguageSelectorContainer';
import { getOrganization } from '../app/App';


const useStyle = makeStyles(() => {
    return {
        root: {
        },

        externalLoginButtonIcon: {
            width : 30,
            height: 30,
        },
    };
}, { name: 'LoginPage' });



interface InputErrors {
    email?: string | undefined;
    password?: string | undefined;
    code?: string | undefined;
    otherError?: string | undefined;
}

export const Login: React.FC = () => {

    const navigate = useNavigate();
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const currentLoginInfo = selectAgentLoginInfo(dataState);
    const { trans } = useRitzTranslation(undefined);
    const [inputErrors, setInputErrors] = useState<InputErrors>({ });
    const routeHelper = RouteHelper.getInstance(navigate);
    const classes = useStyle();
    const organization = getOrganization();


    /**
     * Once we have the organization, check if it has any login scheme. If there is no login scheme, the user can't login therefore
     * we can take the user to the Not Authorized page. Right now we only support `passwordPolicy` so that is the only thing we need to check.
     * We will have other scheme later.
     */
    useEffect(() => {
        if (organization) {
            // If there is no passwordPolicy nor oidcProviders , take the user to notAuthorized page
            if (!organization.authSchemes.passwordPolicy
                && (!organization.authSchemes.oidcProviders || organization.authSchemes.oidcProviders.length === 0)) {
                routeHelper.gotoNotAuthorizedPage();
            }
        }
    }, [organization, routeHelper]);



    const onEmailLoginFormSubmit = useCallback(async (inputs: {
        readonly email: string | undefined,
        password: string | undefined
    }) => {
        if (!organization) {
            return false;
        }

        const { email, password } = inputs;
        const newInputErrors: InputErrors = { };

        if (email && email.length > 0) {
            if (!EMAIL_VALIDATION_REGEX.test(email)) {
                newInputErrors.email = 'error.input.email.invalid';
            }
        } else {
            newInputErrors.email = 'error.input.email.empty';
        }

        if (!password || password.length <= 0) {
            newInputErrors.password = 'error.input.password.empty';
        }

        setInputErrors(newInputErrors);

        if (!newInputErrors.email && !newInputErrors.password && email && password) {
            // email and password passed validation, submit login now
            try {
                const loginInfo = await RitzAPI.getInstance().login(organization.id, email, password);
                setLocalStorageItem(Constants.LS_KEY_LOGIN_INFO, loginInfo);
                await dataDispatch(setLoginInfo(loginInfo));
            } catch (error) {
                if ((error as ApiError).code === 'PASSWORD_EXPIRED') {
                    // If login failed because of 'PASSWORD_EXPIRED' error, show the error message and take the user to
                    // forgot password page so that the user can start the reset password flow.
                    uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));
                    routeHelper.gotoForgotPasswordPage(organization.id);
                } else {
                    setInputErrors({
                        ...inputErrors,
                        otherError: getErrorMessage((error as ApiError).code),
                    });
                }
            }
        }

        return false;
    }, [dataDispatch, inputErrors, organization, routeHelper, uiDispatch]);



    const on2FALoginFormSubmit = useCallback(async (inputs: {readonly code: string | undefined}) => {

        if (!currentLoginInfo || !currentLoginInfo.auth2FToken) {
            return false;
        }

        const { code } = inputs;
        const newInputErrors: InputErrors = { };

        if (code && code.length > 0) {
            if (Number.isNaN(Number(code))) {
                newInputErrors.code = 'error.input.activate_2fa_code.invalid';
            }
        } else {
            newInputErrors.code = 'error.input.activate_2fa_code.empty';
        }

        setInputErrors(newInputErrors);

        if (!newInputErrors.code && code) {
            // code validated, now try login with 2fa code and token
            try {
                const loginInfo = await RitzAPI.getInstance().loginWith2FA(currentLoginInfo.auth2FToken, Number(code));
                setLocalStorageItem(Constants.LS_KEY_LOGIN_INFO, loginInfo);
                await dataDispatch(setLoginInfo(loginInfo));
            } catch (error) {
                setInputErrors({
                    ...inputErrors,
                    otherError: getErrorMessage((error as ApiError).code),
                });
            }
        }

        return false;
    }, [currentLoginInfo, dataDispatch, inputErrors]);


    const onProviderSelected = useCallback(async (providerId: string) => {
        if (!organization) {
            return;
        }

        try {
            const response = await RitzAPI.getInstance().getExternalAuthURL(organization.id, providerId);
            window.location.href = response.authUrl;
        } catch (error) {
            setInputErrors({
                ...inputErrors,
                otherError: getErrorMessage((error as ApiError).code),
            });
        }
    }, [inputErrors, organization]);


    /**
     * Create and cache the list of OIDC provider settings, we will pass these to the LoginForm
     */
    const oidcProvidersSettings = useMemo((): readonly ExternalLoginProviderInfo[] | undefined => {
        if (organization?.authSchemes.oidcProviders && organization.authSchemes.oidcProviders.length > 0) {
            return organization.authSchemes.oidcProviders.map((providerInfo) => {
                return {
                    id              : providerInfo.id,
                    name            : providerInfo.name,
                    loginButtonLabel: trans('login_form.login_with_oidc_provider', { provider_name: providerInfo.name }),
                    icon            : providerInfo.iconUrl
                        ? <img className={classes.externalLoginButtonIcon} src={providerInfo.iconUrl} alt={providerInfo.name} />
                        : undefined,
                };
            });
        }
        return undefined;
    }, [classes.externalLoginButtonIcon, organization?.authSchemes.oidcProviders, trans]);



    if (currentLoginInfo && !currentLoginInfo.authenticated && organization) {
        return (
            <Verify2FAForm
                texts={{
                    title   : trans('dialog.verify_2fa_code.title', { org_name: organization.name }),
                    subtitle: organization.id === DEFAULT_ORGANIZATION_ID
                        ? trans('app.title_default')
                        : trans('app.title', { org_name: organization.name }),
                    verify2FAMessage  : trans('dialog.verify_2fa_code.message'),
                    verifyButtonLabel : trans('label.verify'),
                    authCodeInputLabel: capitalizeAll(trans('label.authentication_code')),
                }}
                errorTexts={{
                    code      : inputErrors.code ? trans(inputErrors.code) : '',
                    otherError: inputErrors.otherError ? trans(inputErrors.otherError) : '',
                }}
                languageSelectorComponent={<LanguageSelectorContainer projectId={undefined} minimal />}
                onFormInputChange={(field: string) => {
                    setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                        draft[field] = undefined;
                        draft.otherError = undefined;
                    }));
                }}
                onFormSubmit={on2FALoginFormSubmit}
            />
        );
    }

    if (!organization) {
        return <NotAuthorized />;
    }

    return (
        <LoginForm
            className={classes.root}
            texts={{
                title   : trans('login_form.title', { org_name: organization.name }),
                subtitle: organization.id === DEFAULT_ORGANIZATION_ID
                    ? trans('app.title_default')
                    : trans('app.title', { org_name: organization.name }),
                emailInputLabel          : capitalizeAll(trans('label.email')),
                passwordInputLabel       : capitalizeAll(trans('label.password')),
                forgetPasswordButtonLabel: trans('login_form.forgot_password'),
                loginButtonLabel         : trans('label.login'),
            }}
            errorTexts={{
                email     : inputErrors.email ? trans(inputErrors.email) : '',
                password  : inputErrors.password ? trans(inputErrors.password) : '',
                otherError: inputErrors.otherError ? trans(inputErrors.otherError) : '',
            }}
            languageSelectorComponent={<LanguageSelectorContainer projectId={undefined} minimal />}

            // If there is oidcProvidersSettings, show the External login options
            externalLogin={
                oidcProvidersSettings ? {
                    providers: oidcProvidersSettings,
                    onProviderSelected,
                } : undefined
            }

            // If there is passwordPolicy, show the email login options
            emailLogin={organization.authSchemes.passwordPolicy ? {
                onGotoForgotPassword: () => {
                    routeHelper.gotoForgotPasswordPage();
                },
                onFormInputChange: (field: string) => {
                    setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                        draft[field] = undefined;
                        draft.otherError = undefined;
                    }));
                },
                onFormSubmit: onEmailLoginFormSubmit,
            } : undefined}
        />
    );

};
